import React, { useState, useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { LanguagePicker } from '../components/LanguagePicker';
import { toast } from 'sonner';
import { Ship, Package, Users, BarChart3, DollarSign, RefreshCw, Send, Loader2, CheckCircle, ArrowRight, Globe, Shield, Clock, ChevronDown } from 'lucide-react';

// Human-friendly labels for feature codes returned by /public/plans
const FEATURE_LABELS = {
  shipments: 'Gestión de embarques',
  containers: 'Control de contenedores',
  events: 'Eventos de tracking',
  internal_notes: 'Notas internas',
  dashboard: 'Dashboard ejecutivo',
  multi_user: 'Multi-usuario',
  client_portal: 'Portal para clientes',
  cost_breakdown: 'Desglose de costos',
  excel_export: 'Exportación a Excel',
  tracking_carriers: 'Tracking automático (navieras)',
  tracking_ports: 'Tracking en puertos',
  tracking_ferrovalle: 'Tracking Ferrovalle',
  certificates: 'Certificados y documentos',
  alerts: 'Alertas automáticas',
  monthly_report: 'Reporte mensual por email',
  sla_dashboard: 'Dashboard de SLA',
  audit: 'Auditoría completa',
  api_access: 'Acceso API',
  priority_support: 'Soporte prioritario',
};

// Fallback plans shown while the public API loads or if it fails
const FALLBACK_PLANS = [
  { code: 'BASIC', name: 'Básico', description: 'Para empezar', price_monthly: 0, features: ['dashboard', 'shipments', 'containers', 'events'] },
  { code: 'PRO', name: 'Profesional', description: 'Para equipos en crecimiento', price_monthly: 499, features: ['tracking_carriers', 'client_portal', 'certificates', 'alerts', 'cost_breakdown'] },
  { code: 'ENTERPRISE', name: 'Enterprise', description: 'Todo ilimitado', price_monthly: 999, features: ['tracking_carriers', 'sla_dashboard', 'audit', 'api_access', 'priority_support'] },
];

export default function LandingPage() {
  const { t } = useI18n();
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Fetch plans from public endpoint so changes in DB reflect on landing automatically
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/plans`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        // Filter: only show active generic plans (not org-type-specific) that have a name and are not internal
        const filtered = (Array.isArray(data) ? data : [])
          .filter(p => p.is_active && !p.org_type)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .slice(0, 3);
        setPlans(filtered);
      })
      .catch(() => setPlans([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.company_name || !contactForm.email || !contactForm.contact_name) {
      toast.error(t('common.contactFormRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(t('common.contactFormSuccess'));
      } else {
        throw new Error('Error al enviar');
      }
    } catch (err) {
      toast.error(t('common.contactFormError'));
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: Ship, title: 'Gestión de Embarques', desc: 'Control total de MBL/BL, estatus, fechas ETD/ETA y documentación.' },
    { icon: Package, title: 'Tracking de Contenedores', desc: 'Semáforo de liberación, días en puerto y estado en tiempo real.' },
    { icon: DollarSign, title: 'Control de Costos', desc: 'Desglose detallado con conversión USD/MXN automática.' },
    { icon: Users, title: 'Portal de Clientes', desc: 'Acceso seguro para que tus clientes vean solo su información.' },
    { icon: BarChart3, title: 'Dashboard & KPIs', desc: 'Métricas clave y reportes para toma de decisiones.' },
    { icon: RefreshCw, title: 'Integraciones', desc: 'Sincronización con Notion y exportación Excel.' },
  ];

  const stats = [
    { value: '500+', label: 'Embarques gestionados' },
    { value: '98%', label: 'Satisfacción' },
    { value: '24/7', label: 'Disponibilidad' },
    { value: '10x', label: 'Más eficiente' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="landing-page">
      {/* Navigation - Fixed */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-ofimex-white.png" 
                alt="OFIMEX" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors">Características</a>
              <a href="#para-quien" className="text-sm text-zinc-400 hover:text-white transition-colors">Para Quién</a>
              <a href="#benefits" className="text-sm text-zinc-400 hover:text-white transition-colors">Beneficios</a>
              <a href="#contacto" className="text-sm text-zinc-400 hover:text-white transition-colors">Contacto</a>
            </div>
            <div className="flex items-center gap-3">
              <LanguagePicker />
              <Link to="/login">
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300">
                  Iniciar Sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover opacity-40"
            poster="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070"
          >
            {/* Video de puerto de contenedores - Pixabay (libre de uso) */}
            <source src="https://cdn.pixabay.com/video/2019/06/14/24366-342689030_large.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/80 via-[#0a0a0f]/60 to-[#0a0a0f]" />
        </div>
        
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse delay-1000" />
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center pt-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm mb-8">
            <Globe className="w-4 h-4" />
            Sistema de Gestión de Importaciones
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Control Total de tus
            <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              Operaciones de Comercio Exterior
            </span>
          </h1>
          
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            La plataforma más completa para gestionar embarques, contenedores y costos. 
            Mantén a tus clientes informados en tiempo real.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contacto">
              <Button size="lg" className="text-lg px-8 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 border-0">
                Solicitar Demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#features">
              <Button size="lg" variant="outline" className="text-lg px-8 border-white/20 text-white hover:bg-white/10">
                Ver Características
              </Button>
            </a>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-zinc-500" />
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative z-10 -mt-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#1a1a2e] to-[#16213e] rounded-2xl p-8 border border-white/10 backdrop-blur-xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-zinc-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Todo lo que necesitas en un solo lugar
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Herramientas diseñadas específicamente para operadores de comercio exterior
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="group relative bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1a] rounded-xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section with Image */}
      <section className="py-24 px-4 relative overflow-hidden" id="benefits">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1769144256181-698b8f807066?q=80&w=2070&auto=format&fit=crop"
            alt="Puerto de contenedores"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-[#0a0a0f]/95 to-[#0a0a0f]" />
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Optimiza tu operación de
                <span className="text-cyan-400"> principio a fin</span>
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Ahorra tiempo</h3>
                    <p className="text-zinc-400">Automatiza tareas repetitivas y reduce errores manuales</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Información segura</h3>
                    <p className="text-zinc-400">Datos encriptados y acceso controlado por roles</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Clientes informados</h3>
                    <p className="text-zinc-400">Portal exclusivo para que tus clientes den seguimiento</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-2xl blur-2xl" />
              <img 
                src="https://images.unsplash.com/photo-1769144256227-5185141c3aca?q=80&w=800&auto=format&fit=crop"
                alt="Contenedores en puerto"
                className="relative rounded-xl border border-white/10 shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Para Quién (Tipos de Organización) */}
      <section className="py-24 px-4 relative" id="para-quien">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0e1a30] to-[#0a0a0f]" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-4">
              <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Para Quién</span>
            </div>
            <h2 className="text-4xl font-bold text-white mb-3">Diseñado para tu rol en la cadena</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">OFIMEX se adapta a tu tipo de organización con vistas y herramientas específicas</p>
          </div>
          <div className="grid md:grid-cols-1 max-w-2xl mx-auto gap-6">
            {/* Comercializadora */}
            <div className="group relative bg-gradient-to-br from-blue-500/5 to-blue-900/5 rounded-2xl p-8 border border-blue-500/20 hover:border-blue-400/50 transition-all">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-blue-400 font-semibold">COM</p>
                  <h3 className="text-xl font-bold text-white">Comercializadoras</h3>
                </div>
              </div>
              <p className="text-zinc-400 mb-5">Controla embarques, contenedores, costos y márgenes. Integra proveedores, clientes y pagos en un solo lugar.</p>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Dashboard con KPIs de compra-venta</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Tracking tiempo real + SLA automáticos</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Facturas, Packing List, Carta Explicativa</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Portal exclusivo para clientes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
{/* Planes section temporarily removed — pricing under review. */}

      {/* Contact Section */}
      <section className="py-24 px-4 relative" id="contacto">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0f1629] to-[#0a0a0f]" />
        
        <div className="relative max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">¿Listo para empezar?</h2>
            <p className="text-zinc-400 text-lg">
              Déjanos tus datos y te contactaremos para una demostración personalizada
            </p>
          </div>
          
          {submitted ? (
            <div className="bg-[#1a1a2e] rounded-2xl p-12 text-center border border-white/10">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">¡Gracias por tu interés!</h3>
              <p className="text-zinc-400">
                Hemos recibido tu información. Un asesor se pondrá en contacto contigo pronto.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#1a1a2e] rounded-2xl p-8 border border-white/10 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name" className="text-zinc-300">Nombre de la Empresa *</Label>
                  <Input
                    id="company_name"
                    value={contactForm.company_name}
                    onChange={(e) => setContactForm({...contactForm, company_name: e.target.value})}
                    placeholder="Tu empresa"
                    className="mt-2 bg-[#0f0f1a] border-white/10 text-white placeholder:text-zinc-600 focus:border-cyan-500"
                    required
                    data-testid="contact-company"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name" className="text-zinc-300">Tu Nombre *</Label>
                  <Input
                    id="contact_name"
                    value={contactForm.contact_name}
                    onChange={(e) => setContactForm({...contactForm, contact_name: e.target.value})}
                    placeholder="Nombre completo"
                    className="mt-2 bg-[#0f0f1a] border-white/10 text-white placeholder:text-zinc-600 focus:border-cyan-500"
                    required
                    data-testid="contact-name"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-zinc-300">Correo Electrónico *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    placeholder="correo@empresa.com"
                    className="mt-2 bg-[#0f0f1a] border-white/10 text-white placeholder:text-zinc-600 focus:border-cyan-500"
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300">Teléfono</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    placeholder="+52 ..."
                    className="mt-2 bg-[#0f0f1a] border-white/10 text-white placeholder:text-zinc-600 focus:border-cyan-500"
                    data-testid="contact-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="message" className="text-zinc-300">¿Qué te gustaría saber? (opcional)</Label>
                <Textarea
                  id="message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  placeholder="Cuéntanos sobre tu operación..."
                  className="mt-2 bg-[#0f0f1a] border-white/10 text-white placeholder:text-zinc-600 focus:border-cyan-500"
                  rows={4}
                  data-testid="contact-message"
                />
              </div>
              
              <Button 
                type="submit" 
                size="lg"
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-lg"
                disabled={submitting} 
                data-testid="contact-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Solicitar Información
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-ofimex-white.png" 
                alt="OFIMEX" 
                className="h-8 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-zinc-500">
              © 2026 OFIMEX. Sistema de gestión de operaciones de importación.
            </p>
            <div className="flex gap-6">
              <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors">Características</a>
              <a href="#contacto" className="text-sm text-zinc-400 hover:text-white transition-colors">Contacto</a>
              <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors">Acceso</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
